import { followTeams, WithSkeleton } from '@capsicum/common';
import { Divider, Image, Text, useRouter } from '@capsicum/common-web';
import {
	useWCPledgeBasketTexts,
	type WCPledgeBasketItemWithTeamData
} from '@capsicum/features/world-championship-pledge/shared';
import { useMemo } from 'react';
import styled from 'styled-components';

import { WCPledgeBasketDetailsCard, WCPledgeBasketDetailsCardSkeleton } from './WCPledgeBasketDetailsCard';

const SKELETON_ARRAY_LENGTH = 4;

const WCPledgeBasketDetailsCardListSkeleton = () => (
	<S.CardGroup>
		{Array.from({ length: SKELETON_ARRAY_LENGTH }).map((_, index) => (
			<div key={`skeleton-card-${index}`}>
				<WCPledgeBasketDetailsCardSkeleton />
				{index !== SKELETON_ARRAY_LENGTH - 1 && <Divider orientation="horizontal" />}
			</div>
		))}
	</S.CardGroup>
);

const AddCountryRow = ({ disabled }: { disabled?: boolean }) => {
	const { intlTexts } = useWCPledgeBasketTexts();
	const { push } = useRouter();
	const handleClick = () => {
		if (disabled) return;

		push('/locker-room-internationals');
	};

	return (
		<S.AddCountryRow
			$disabled={disabled}
			data-testid="wcPledge-basketDetails-addCountryButton"
			onClick={handleClick}
		>
			<Image alt="add-country-icon" src={followTeams} style={{ height: 26, width: 26 }} />
			<Text color={disabled ? 'main.disabled' : 'main.enabled'} variant="text3">
				{intlTexts.addCountry}
			</Text>
		</S.AddCountryRow>
	);
};

const WCPledgeBasketDetailsCardListComponent = ({
	basketDetailsList,
	focusedTeamId,
	maxTeamsPerPledge,
	onAmountChange,
	onDelete
}: {
	basketDetailsList: Array<WCPledgeBasketItemWithTeamData>;
	focusedTeamId: null | string;
	maxTeamsPerPledge: number;
	onAmountChange: (teamId: string, amount: number) => void;
	onDelete: (teamId: string) => void;
}) => {
	const { intlTexts } = useWCPledgeBasketTexts();
	const showEmptyBasket: boolean = basketDetailsList.length <= 0;
	const isMaxTeamsReached: boolean = basketDetailsList.length === maxTeamsPerPledge;

	const basketDetailsContent = useMemo(() => {
		return (
			<S.CardGroup>
				<S.HeaderRow $showEmptyBasket={showEmptyBasket}>
					{showEmptyBasket ? (
						<>
							<Text testID="wcPledge-basketDetails-emptyBasketTitle" variant="text1">
								{intlTexts.emptyBasketTitle}
							</Text>
							<Text variant="text3">{intlTexts.emptyBasketSubtitle}</Text>
						</>
					) : (
						<>
							<Text variant="text2">{intlTexts.country}</Text>
							<Text variant="text2">{intlTexts.amountToPledge}</Text>
						</>
					)}
				</S.HeaderRow>

				<Divider orientation="horizontal" />

				{!showEmptyBasket &&
					basketDetailsList.map((team, index) => (
						<div key={`card-${team.id}`}>
							<WCPledgeBasketDetailsCard
								focusedTeamId={focusedTeamId}
								pledgeAmount={team.amount}
								teamId={team.id}
								teamImage={team.flagUrl}
								teamName={team.name}
								onAmountChange={onAmountChange}
								onDelete={onDelete}
							/>
							{index !== basketDetailsList.length && <Divider key={index} orientation="horizontal" />}
						</div>
					))}
				<AddCountryRow disabled={isMaxTeamsReached} />
			</S.CardGroup>
		);
	}, [intlTexts, basketDetailsList, showEmptyBasket, isMaxTeamsReached, focusedTeamId, onAmountChange, onDelete]);

	return basketDetailsContent;
};

export const WCPledgeBasketDetailsCardList = WithSkeleton(
	WCPledgeBasketDetailsCardListComponent,
	WCPledgeBasketDetailsCardListSkeleton
);

namespace S {
	export const CardGroup = styled.div`
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		display: flex;
		flex-flow: column;
		${({ theme }) => theme.border.radius.medium};
		overflow: hidden;
	`;

	export const HeaderRow = styled.div<{ $showEmptyBasket?: boolean }>`
		display: flex;
		align-items: center;
		justify-content: ${({ $showEmptyBasket }) => ($showEmptyBasket ? 'center' : 'space-between')};
		flex-direction: ${({ $showEmptyBasket }) => ($showEmptyBasket ? 'column' : 'row')};
		padding: ${({ $showEmptyBasket, theme }) => ($showEmptyBasket ? theme.spacings.medium : theme.spacings.small)};
	`;

	export const AddCountryRow = styled.div<{ $disabled?: boolean }>`
		display: flex;
		gap: ${({ theme }) => theme.spacings.small};
		align-items: center;
		padding: ${({ theme }) => theme.spacings.small};
		cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
	`;

	export const AddCountryRowIcon = styled(Image)<{ $disabled?: boolean }>`
		opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
	`;
}
